import type { QueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/queryKeys'

/**
 * **The bell's badge and its panel read the same truth — invalidate them together.**
 *
 * `GET /notifications/unread-count` (the badge, `queryKeys.notifications.unreadCount`) and
 * `GET /notifications` (the panel's list, `queryKeys.notifications.list`) are two DIFFERENT
 * endpoints, not one endpoint under two keys — so, unlike `galleryDetail`/`teamRoster`/etc.,
 * this pair sits outside `api/cacheGroups.ts`, whose guard (`queryKeyCacheGroups.test.ts`)
 * only tracks the "one endpoint, several keys" shape. But a studio member reading the bell
 * expects the two to agree regardless: a nonzero badge next to an empty panel reads as "the
 * app is broken," not "one of two independent caches hasn't caught up." This is the same
 * "one named helper, not two invalidateQueries calls" shape used for the roster
 * (`features/team/lib/rosterInvalidation.ts`) and the signed-in member's own record
 * (`features/settings/lib/meRecordInvalidation.ts`), applied here across endpoints instead
 * of across keys on one endpoint.
 *
 * G2-J3-F7 / G2-J1b-F10 / G2-J6ab-F8 all filed the same symptom ("badge says 3, panel says
 * 'No notifications'"). The actual root cause was the panel's `enabled: notifOpen` query
 * racing its own first fetch against the render (see AppLayout.tsx's notifications-list
 * `useQuery` and its loading-state guard in `notifDropdownInner`) — every existing mutation
 * site already invalidated both keys. This helper is the belt to that fix's suspenders: it
 * makes "both together" the only way to invalidate either, so a future call site can't
 * invalidate the count and forget the list (or vice versa).
 */
export const NOTIFICATIONS_QUERY_KEYS = [
  queryKeys.notifications.list,
  queryKeys.notifications.unreadCount,
] as const

/**
 * Mark both the badge and the panel stale. Awaitable, so a caller that closes the dropdown
 * on success can do it after both are known to be refetching.
 */
export function invalidateNotifications(qc: QueryClient): Promise<void> {
  return Promise.all(
    NOTIFICATIONS_QUERY_KEYS.map((queryKey) => qc.invalidateQueries({ queryKey })),
  ).then(() => undefined)
}
